// hooks/useCommonHead.tsx
import { type HeadProps } from "gatsby"
import React from 'react'

interface SEOData {
  title?: string
  description?: string
  image?: string
  pathname?: string
  robots?: string
}

export const useCommonHead = ({ location, data }: HeadProps & { data?: SEOData }) => {
  const baseURL = "https://www.salonteq.com"
  const isAr = location.pathname.includes('/ar/') || location.pathname.startsWith('/ar');

  const defaultTitle = isAr 
    ? "صالون تك | ابحث واحجز صالونات التجميل والسبا في قطر" 
    : "SalonTeq | Discover & Book Salons, Spas & Grooming in Qatar";
    
  const defaultDescription = isAr 
    ? "صالون تك هو المنصة الرائدة في قطر للبحث وحجز مواعيد صالونات التجميل، السبا، ومراكز العناية الشخصية مع عروض وخصومات حصرية."
    : "SalonTeq is Qatar's premier platform to discover and book beauty salons, luxury spas, and grooming services with exclusive offers and verified reviews.";
  
  const defaultKeywords = isAr
    ? "صالون, صالونات تجميل قطر, سبا الدوحة, حجز صالونات, صالون تجميل نسائي, صالون رجالي, عروض صالونات, صالون تك"
    : "salons, beauty salons Qatar, spa Doha, salon booking app, hair salon, nail spa, grooming services Qatar, salon offers, SalonTeq";

  const defaultImage = "/logo.png"
  
  const seo = {
    title: data?.title || defaultTitle,
    description: data?.description || defaultDescription,
    image: data?.image ? (data.image.startsWith('http') ? data.image : `${baseURL}${data.image}`) : `${baseURL}${defaultImage}`,
    url: `${baseURL}${location.pathname}`,
    keywords: defaultKeywords,
    robots: data?.robots || "index, follow"
  }

  return {
    // Basic Meta Tags
    basicMeta: (
      <>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />
        <meta name="robots" content={seo.robots} />
        <meta name="author" content="SalonTeq" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="icon" type="image/svg+xml" href="/logo.png" />
      </>
    ),

    // Open Graph Meta Tags
    openGraphMeta: (
      <>
        <meta property="og:site_name" content="SalonTeq" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.url} />
        <meta property="og:image" content={seo.image} />
        <meta property="og:image:alt" content="SalonTeq Logo" />
        <meta property="og:image:width" content="400" />
        <meta property="og:image:height" content="400" />
      </>
    ),

    // Twitter Meta Tags
    twitterMeta: (
      <>
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={seo.image} />
        <meta name="twitter:image:alt" content="SalonTeq Logo" />
      </>
    ),

    // Microsoft/PWA Meta Tags
    microsoftMeta: (
      <>
        <meta httpEquiv="X-UA-Compatible" content="ie=edge" />
        <meta name="theme-color" content="#c89740" />
        <meta name="msapplication-navbutton-color" content="#c89740" />
        <meta name="apple-mobile-web-app-status-bar-style" content="#c89740" />
        <meta name="msapplication-TileColor" content="#c89740" />
        <meta name="msapplication-TileImage" content="/logo.png" />
        <meta name="application-name" content="SalonTeq" />
        <meta name="msapplication-tooltip" content="SalonTeq" />
        <meta name="msapplication-starturl" content="/" />
      </>
    ),

    // Resource Hints
    resourceHints: (
      <>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
        <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
        <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      </>
    ),

    // Fonts
    fonts: (
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined" rel="stylesheet" />
    ),

    // Schema.org JSON-LD
    jsonLd: (
      <script type="application/ld+json">
        {JSON.stringify(getOrganizationSchema(baseURL, isAr))}
      </script>
    ),
  }
}

const getOrganizationSchema = (baseURL: string, isAr: boolean) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "SalonTeq",
  "url": baseURL,
  "logo": {
    "@type": "ImageObject",
    "url": `${baseURL}/logo.png`,
    "width": "400",
    "height": "400"
  },
  "description": isAr 
    ? "صالون تك هو المنصة الرائدة في قطر للبحث وحجز مواعيد صالونات التجميل، السبا، ومراكز العناية الشخصية مع عروض وخصومات حصرية."
    : "SalonTeq is Qatar's premier platform to discover and book beauty salons, luxury spas, and grooming services with exclusive offers and verified reviews.",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "QA",
    "addressLocality": "Doha"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer support",
    "email": "support@salonteq.com"
  }
})